<template>
  <div class="site">

    <!-- Hero band -->
    <section class="hero">
      <div class="hero-title">
        商用级图像生成
      </div>
      <div class="hero-sub">
        以企业级体验为目标，提供更清晰的操作流程与更稳定的反馈。
      </div>
    </section>

    <main class="shell">
      <!-- Control panel -->
      <section class="panel form-panel">
          <div class="panel-head">
          <div class="panel-title">提示词设计器</div>
          <div class="panel-desc">撰写你的创意提示词，选择尺寸与偏好，然后点击生成。</div>
        </div>

        <form @submit.prevent="onGenerate" class="form">
          <label class="field">
            <span class="label">Prompt</span>
            <textarea
              v-model="prompt"
              rows="7"
              class="input input-area"
              placeholder="例如：都市夜景中一位身着高级时装的女性，电影级灯光，8k，细节丰富..."
              required
            ></textarea>
          </label>

          <div class="row two">
            <label class="field">
              <span class="label">反向提示词</span>
              <input
                v-model="negativePrompt"
                type="text"
                class="input"
                placeholder="避免内容（如：低质量、文字、畸形、噪点）"
              />
            </label>

            <label class="field">
              <span class="label">尺寸</span>
              <!-- 使用 Element Plus 的 Select 组件 -->
              <el-select v-model="size" placeholder="选择尺寸" style="width: 200px;">
                <el-option label="1664 × 928 （16:9）" value="1664*928" />
                <el-option label="1472 × 1140 （4:3）" value="1472*1140" />
                <el-option label="1328 × 1328 （1:1）" value="1328*1328" />
                <el-option label="1140 × 1472 （3:4）" value="1140*1472" />
                <el-option label="928 × 1664 （9:16）" value="928*1664" />
              </el-select>
            </label>
          </div>

          <div class="row toggles">
            <label class="toggle" title="扩展提示意思是自动补充并完善关键词" aria-label="扩展提示">
              <input type="checkbox" v-model="promptExtend" />
              <span title="扩展提示意思是自动补充并完善关键词">扩展提示</span>
            </label>
          </div>

          <div class="row btns">
            <button class="btn primary" :disabled="loading">
              <span v-if="!loading">生成</span>
              <span v-else>生成中…</span>
            </button>
            <button type="button" class="btn ghost" @click="clearAll" :disabled="loading">清除</button>
          </div>

        </form>
      </section>

      <!-- Result panel -->
      <section class="panel results-panel">
          <div class="panel-head">
          <div class="panel-title">结果</div>
          <div class="panel-desc">
            <span v-if="lastResponseTime">耗时：{{ lastResponseTime }} 毫秒</span>
            <span v-if="error" class="error"> · {{ error }}</span>
          </div>
        </div>

        <!-- 16s progress bar -->
        <div v-show="progress > 0" class="progress">
          <div class="bar" :style="{ width: progress + '%' }"></div>
          <div class="progress-meta">
            <span>{{ progress }}%</span>
            <span v-if="loading" class="hint">正在生成，请稍候…</span>
          </div>
        </div>

        <div v-if="loading && images.length === 0" class="skeletons">
          <div class="sk"></div>
        </div>

        <div v-if="!loading && images.length === 0" class="empty">
          还没有图像，先在左侧输入提示词并点击生成。
        </div>

        <div v-else class="gallery">
          <div v-for="(img, idx) in images" :key="idx" class="thumb">
              <div class="thumb-inner">
              <img :src="img" :alt="'生成图 ' + idx" @click="openViewer(idx)" oncontextmenu="return false;" />
            </div>
            <div class="thumb-actions">
              <button type="button" class="link" @click="openViewer(idx)" title="查看大图">
                <font-awesome-icon :icon="['fas','eye']" /> 查看大图
              </button>
              <button type="button" class="link" @click="downloadImage(img)" title="下载">
                <font-awesome-icon :icon="['fas','download']" /> 下载
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- Viewer -->
    <el-image-viewer
      v-if="viewerVisible"
      :url-list="images"
      :initial-index="viewerIndex"
      @close="closeViewer"
      teleported
    />
  </div>
</template>

<script setup>
import { ref } from 'vue'
import axios from 'axios'
import { ElNotification } from 'element-plus'

const prompt = ref('')
const negativePrompt = ref('')
const size = ref('1328*1328')
const promptExtend = ref(true)

const loading = ref(false)
const images = ref([])
const error = ref(null)
const lastResponseTime = ref(null)
const userInfo = ref(null)

// viewer (Element Plus)
const viewerVisible = ref(false)
const viewerIndex = ref(0)
function openViewer(i) {
  viewerIndex.value = i
  viewerVisible.value = true
}
function closeViewer() {
  viewerVisible.value = false
}

// 16s progress bar
const progress = ref(0)
let progressTimer = null
function clearProgress() {
  if (progressTimer) {
    clearInterval(progressTimer)
    progressTimer = null
  }
  progress.value = 0
}
function startProgress(durationMs = 16000) {
  clearProgress()
  const t0 = performance.now()
  progress.value = 1
  progressTimer = setInterval(() => {
    const elapsed = performance.now() - t0
    const pct = Math.min(99, Math.round((elapsed / durationMs) * 100))
    progress.value = pct
    if (pct >= 99) {
      clearInterval(progressTimer)
      progressTimer = null
    }
  }, 100)
}
function finishProgress() {
  if (progressTimer) {
    clearInterval(progressTimer)
    progressTimer = null
  }
  progress.value = 100
  setTimeout(() => { progress.value = 0 }, 900)
}

function clearAll() {
  prompt.value = ''
  negativePrompt.value = ''
  images.value = []
  error.value = null
  lastResponseTime.value = null
  clearProgress()
}

// 检查用户信息
async function checkUserInfo() {
  try {
    // 检查缓存中是否有用户信息
    const cachedUserInfo = localStorage.getItem('userInfo');
    if (cachedUserInfo) {
      userInfo.value = JSON.parse(cachedUserInfo);
      ElNotification.info({
        title: '信息',
        message: '找到缓存的用户信息',
        duration: 1000
      });
      return true;
    }

    // 如果没有缓存，则从服务器获取用户信息
    const response = await axios.get('https://creatinf.com/user_info', {
      withCredentials: true
    });

    if (response.data.success) {
      const userData = response.data.data || response.data.user;
      if (userData) {
        userInfo.value = userData;
        // 缓存用户信息
        localStorage.setItem('userInfo', JSON.stringify(userData));
        ElNotification.success({
          title: '成功',
          message: '用户信息已从服务器验证',
          duration: 1000
        });
        return true;
      }
    }

    userInfo.value = null;
    return false;
  } catch (error) {
    ElNotification.error({
      title: '错误',
      message: '检查用户信息失败: ' + error.message,
      duration: 3000
    });
    userInfo.value = null;
    return false;
  }
}

async function fetchImageAsBlob(url) {
  try {
    const response = await axios.get(url, { responseType: 'blob' });
    const blob = response.data;
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Failed to fetch image as blob:', error);
    return null;
  }
}

// 检查下载配额
async function checkDownloadQuota() {
  try {
    const response = await axios.post('https://creatinf.com/check_download_quota', {
      modelType: 't2i'
    }, {
      withCredentials: true,
    });

    return {
      success: response.data.success,
      error: response.data.error,
      message: response.data.message,
      quotaInfo: response.data.quotaInfo
    };
  } catch (error) {
    ElNotification.error({
      title: '错误',
      message: '检查下载配额失败: ' + (error.response?.data?.error || error.message),
      duration: 3000
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
}

// 更新用户配额信息
function updateUserQuotaInfo(quotaInfo) {
  if (quotaInfo) {
    userInfo.value = {
      ...userInfo.value,
      daily_free_images: quotaInfo.daily_free_images,
      remaining_images: quotaInfo.remaining_images
    };

    // 更新本地存储
    localStorage.setItem('userInfo', JSON.stringify(userInfo.value));
  }
}


async function onGenerate() {
  if (!prompt.value.trim()) {
    error.value = '提示词不能为空'
    return
  }
  loading.value = true
  error.value = null
  startProgress(16000)

  const payload = {
    prompt: prompt.value,
    negative_prompt: negativePrompt.value,
    size: size.value,
    prompt_extend: promptExtend.value
  }

  const t0 = performance.now()
  try {
    const apiUrl = process.env.NODE_ENV === 'development'
      ? 'http://127.0.0.1:9001/api/generate_image'
      : 'https://creatinf.com/api/generate_image'
    const resp = await axios.post(apiUrl, payload, { timeout: 120000 })
    const dt = Math.round(performance.now() - t0)
    lastResponseTime.value = dt

    if (resp.data && resp.data.ok) {
      const urls = resp.data.urls || [];
      const blobUrls = [];
      for (const url of urls) {
        if (url && url.startsWith('http')) {
          const blobUrl = await fetchImageAsBlob(url);
          if (blobUrl) {
            blobUrls.push(blobUrl);
          }
        }
      }
      images.value = blobUrls;
      
      finishProgress();
      if (images.value.length === 0) {
        error.value = '生成成功，但未返回有效图片。';
      }
    } else {
      error.value = resp?.data?.error || '生成失败';
      clearProgress();
    }
  } catch (e) {
    error.value = e?.response?.data?.error || e?.message || '网络错误'
    clearProgress()
  } finally {
    loading.value = false
  }
}

async function downloadImage(blobUrl) {
  try {
    // 检查登录状态
    const isLoggedIn = await checkUserInfo();

    if (!isLoggedIn || !userInfo.value) {
      ElNotification.error({
        title: '错误',
        message: '请先登录，下载功能需要登录后才能使用',
        duration: 3000
      });
      error.value = '请先登录，下载功能需要登录后才能使用';
      return;
    }

    // 检查下载配额
    const quotaResponse = await checkDownloadQuota();

    if (!quotaResponse.success) {
      error.value = quotaResponse.error || '配额不足，无法下载';
      return;
    }

    // 更新用户配额信息
    if (quotaResponse.quotaInfo) {
      updateUserQuotaInfo(quotaResponse.quotaInfo);
    }

    // 执行下载
    const link = document.createElement('a');
    link.href = blobUrl; // 直接使用传入的 blobUrl
    
    // 从 Blob URL 中提取文件名，或者使用默认名称
    let filename = 'generated-image.png';
    // Blob URL 通常不包含文件名信息，这里可以考虑从原始 prompt 生成文件名
    // 或者在生成 Blob URL 时就附带文件名信息
    // 暂时使用默认文件名
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // 注意：这里不应该 revokeObjectURL(blobUrl)，因为 blobUrl 还在 images 数组中被使用
    // 只有当图片不再需要显示时才应该调用 URL.revokeObjectURL(blobUrl)

    // 显示成功消息
    ElNotification.success({
      title: '成功',
      message: (quotaResponse.message || '图片已成功下载') + '，扣费5点',
      duration: 3000
    });

  } catch (err) {
    ElNotification.error({
      title: '错误',
      message: '下载过程出错: ' + (err.response?.data?.error || err.message || '下载失败'),
      duration: 3000
    });
    error.value = err.response?.data?.error || err.message || '下载失败';
  }
}
</script>


<style scoped>
/* ===== 基础重置与字体 ===== */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  background: linear-gradient(135deg, #ff6b6b 0%, #ffa726 100%);
  min-height: 100vh;
  line-height: 1.5;
  color: #2d3748;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.site {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ===== 顶部导航 ===== */
.nav {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 1rem 2rem;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.brand {
  display: flex;
  align-items: center;
}

.logo {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #ff6b6b 0%, #ffa726 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
}

/* ===== 英雄区域 ===== */
.hero {
  text-align: center;
  padding: 4rem 2rem;
  color: white;
  background: linear-gradient(135deg, rgba(255, 107, 107, 0.9) 0%, rgba(255, 167, 38, 0.9) 100%);
  position: relative;
  overflow: hidden;
}

.hero::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
  pointer-events: none;
}

.hero-title {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
  position: relative;
  z-index: 1;
}

.hero-sub {
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  font-weight: 400;
}

/* ===== 主体内容 ===== */
.shell {
  flex: 1;
  padding: 3rem 2rem;
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 3rem;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
}

/* ===== 面板样式 ===== */
.panel {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.panel:hover {
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.panel-head {
  margin-bottom: 2rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding-bottom: 1.5rem;
}

.panel-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #ff6b6b 0%, #ffa726 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.panel-desc {
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.6;
}

.panel-desc .error {
  color: #ef4444;
  font-weight: 600;
}

/* ===== 表单样式 ===== */
.form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.label {
  font-weight: 600;
  color: #374151;
  font-size: 0.95rem;
}

.input {
  padding: 0.875rem 1.125rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
}

.input:focus {
  outline: none;
  border-color: #ff6b6b;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 0 0 4px rgba(255, 107, 107, 0.1);
}

.input-area {
  resize: vertical;
  min-height: 140px;
  font-family: inherit;
  line-height: 1.6;
}

.input::placeholder {
  color: #9ca3af;
}

/* ===== 行布局 ===== */
.row {
  display: flex;
  gap: 1.5rem;
}

.row.two {
  grid-template-columns: 1fr 200px;
}

.row.toggles {
  gap: 2rem;
  margin: 0.5rem 0;
}

.row.btns {
  margin-top: 1rem;
  gap: 1rem;
}

/* ===== 切换开关 ===== */
.toggle {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  user-select: none;
  font-weight: 500;
  color: #374151;
}

.toggle input[type="checkbox"] {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid #d1d5db;
  appearance: none;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
}

.toggle input[type="checkbox"]:checked {
  background: #ff6b6b;
  border-color: #ff6b6b;
}

.toggle input[type="checkbox"]:checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 12px;
  font-weight: bold;
}

/* ===== 按钮样式 ===== */
.btn {
  padding: 0.875rem 2rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-decoration: none;
  min-height: 48px;
  position: relative;
  overflow: hidden;
}

.btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.btn:hover::before {
  left: 100%;
}

.btn.primary {
  background: linear-gradient(135deg, #ff6b6b 0%, #ffa726 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
}

.btn.primary:hover {
  box-shadow: 0 8px 25px rgba(255, 107, 107, 0.4);
  transform: translateY(-2px);
}

.btn.primary:active {
  transform: translateY(0);
}

.btn.ghost {
  background: rgba(255, 255, 255, 0.8);
  color: #ff6b6b;
  border: 2px solid rgba(255, 107, 107, 0.2);
}

.btn.ghost:hover {
  background: rgba(255, 107, 107, 0.05);
  border-color: rgba(255, 107, 107, 0.3);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.btn:disabled::before {
  display: none;
}

/* ===== 免责声明 ===== */
.disclaimer {
  font-size: 0.85rem;
  color: #64748b;
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(255, 165, 0, 0.05);
  border-left: 4px solid #fbbf24;
  border-radius: 8px;
  line-height: 1.5;
}

/* ===== 进度条 ===== */
.progress {
  margin: 1.5rem 0;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  height: 8px;
}

.progress .bar {
  height: 100%;
  background: linear-gradient(90deg, #ff6b6b, #ffa726);
  border-radius: 10px;
  transition: width 0.3s ease;
  position: relative;
}

.progress .bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-image: linear-gradient(45deg, rgba(255, 255, 255, .2) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, .2) 50%, rgba(255, 255, 255, .2) 75%, transparent 75%, transparent);
  background-size: 20px 20px;
  animation: move 1s linear infinite;
}

@keyframes move {
  0% { background-position: 0 0; }
  100% { background-position: 20px 20px; }
}

.progress-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: #64748b;
}

.progress-meta .hint {
  font-style: italic;
}

/* ===== 骨架屏 ===== */
.skeletons {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

.sk {
  aspect-ratio: 1;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ===== 空状态 ===== */
.empty {
  text-align: center;
  padding: 4rem 2rem;
  color: #64748b;
  font-size: 1.1rem;
}

.empty::before {
  content: '🎨';
  display: block;
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

/* ===== 图片画廊 ===== */
.gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

.thumb {
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.thumb:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
}

.thumb-inner {
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  cursor: pointer;
}

.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.thumb-inner:hover img {
  transform: scale(1.05);
}

.thumb-actions {
  padding: 1rem;
  display: flex;
  gap: 0.75rem;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
}

.link {
  flex: 1;
  padding: 0.5rem;
  text-align: center;
  color: #ff6b6b;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 107, 107, 0.2);
}

.link:hover {
  background: rgba(255, 107, 107, 0.1);
  border-color: rgba(255, 107, 107, 0.3);
}

/* ===== 查看器 ===== */
.viewer {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(20px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
}

.viewer-box {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.viewer img {
  max-width: 100%;
  max-height: calc(90vh - 100px);
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.close {
  position: absolute;
  top: -3rem;
  right: -1rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 1.5rem;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.viewer-nav {
  display: flex;
  gap: 1rem;
}

/* ===== 底部 ===== */
.foot {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding: 1.5rem 2rem;
  text-align: center;
  color: #64748b;
  font-size: 0.9rem;
}

/* ===== 响应式设计 ===== */
@media (max-width: 1024px) {
  .shell {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 2rem 1rem;
  }
  
  .hero-title {
    font-size: 2.5rem;
  }
  
  .panel {
    padding: 2rem;
  }
}

@media (max-width: 768px) {
  .nav {
    padding: 1rem;
  }
  
  .hero {
    padding: 3rem 1rem;
  }
  
  .hero-title {
    font-size: 2rem;
  }
  
  .hero-sub {
    font-size: 1rem;
  }
  
  .shell {
    padding: 1.5rem 1rem;
  }
  
  .panel {
    padding: 1.5rem;
    border-radius: 16px;
  }
  
  .row {
    flex-direction: column;
    gap: 1rem;
  }
  
  .row.toggles {
    flex-direction: row;
    gap: 1.5rem;
  }
  
  .row.btns {
    flex-direction: row;
  }
  
  .gallery {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
  }
  
  .hide-mobile {
    display: none;
  }
  
  .viewer {
    padding: 1rem;
  }
  
  .viewer-box {
    max-width: 95vw;
    max-height: 95vh;
  }
  
  .close {
    top: -2rem;
    right: 0;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 1.75rem;
  }
  
  .panel-title {
    font-size: 1.25rem;
  }
  
  .thumb {
    width: 200px;
  }
  
  .sk {
    width: 200px;
  }
  
  .btn {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
  }
}

/* ===== 高级视觉效果 ===== */
.panel::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 107, 107, 0.3), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.panel:hover::before {
  opacity: 1;
}

/* 滚动条样式 */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #ff6b6b 0%, #ffa726 100%);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #ff5252 0%, #ff9800 100%);
}

/* 选中文本样式 */
::selection {
  background: rgba(255, 107, 107, 0.2);
  color: inherit;
}

/* Element Plus Select 样式覆盖 */
.field :deep(.el-select) {
  width: 200px !important; /* 固定宽度为200px */
}

.field :deep(.el-select .el-select__wrapper) {
  width: 200px !important;
}

.field :deep(.el-select .el-input__wrapper) {
  width: 200px !important;
  min-height: 40px;
  padding: 0 0.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(6px);
  box-shadow: none !important;
  display: flex;
  align-items: center;
}

.field :deep(.el-select .el-input.is-focus .el-input__wrapper) {
  border-color: #ff6b6b !important;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 0 0 4px rgba(255, 107, 107, 0.1) !important;
}

.field :deep(.el-select .el-input__inner) {
  line-height: 1.5;
  color: #2d3748;
}
  
</style>
